import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { getLatestPubSLEvent } from "../../api/seniority";
import { getEligibleEmployeesWithInvalidBalances } from "../../util/bid/IAMFTOBidUtils";
import { isAwardedBid, isClosedBid, isDeletedBid, isOpenBid } from "../../util/bid/bidCommon.js";
import { BID_GROUP_FILTER_OPTIONS } from "../../util/bid/bidConstants";
import {
  getEligibleEmployees,
  getEmployeeUnrankedCount,
  getIneligibleEmployees,
  getNonRemovedEmployees,
  handleRankCellEditCommit,
  handleRowOrderChange,
  jumpToSearchedEmployee,
} from "../../util/bid/bidGroupUtils";
import { getScrubbedSeniorityListForFTO } from "../../util/bid/bidScrubber";
import {
  getBidGroupColumns,
  getEligibleEmployeesWithInvalidBalanceCount,
  handleBalanceCellEditCommit,
} from "../../util/bid/ftoBidUtils";
import { INVALID_FTO_BALANCE } from "../../util/bid/messageConstants";
import { initializeSearchIndex } from "../../util/bid/searchBidGroup";
import { calcDailyAllotmentInitialState } from "../../util/bid/timeOffBidUtils";
import { copyJsonObject } from "../../util/commonUtils.js";
import { BID_GROUP_PAGE_TYPE, GET_BID_SENIORITY_LIST_TYPE } from "../../util/constants";
import {
  BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE,
  ERROR_MESSAGE,
  INVALID_BID_GROUP_RANK,
} from "../../util/messageConstants";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay.jsx";
import customLoadingTableOverlay from "../CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay";
import ConfirmButton from "../fundamentals/ConfirmButton";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import WarningDialog from "../fundamentals/WarningDialog.jsx";
import AddEmployeeDialog from "./AddEmployeeDialog";
import BalancesImport from "./BalancesImport";
import BidGroupEmpAuditHistoryDialog from "./BidGroupEmpAuditHistoryDialog";
import BidGroupExportCsv from "./BidGroupExportCsv";
import BidGroupSearchBox from "./BidGroupSearchBox";
import EmployeeEligibilityDialog from "./EmployeeEligibilityDialog";
import ReAddEmployeeDialog from "./ReAddEmployeeDialog";
import RemoveEmployeeDialog from "./RemoveEmployeeDialog";

const useStyles = makeStyles((theme) => ({
  employeeGroupList: {
    "& div": {
      "& .MuiDataGrid-footerContainer": {
        display: "none",
      },
    },
  },
  newEmployee: {
    backgroundColor: theme.palette.secondary.swaLightBlue + " !important",
  },
  removedEmployeeRow: {
    backgroundColor: theme.palette.secondary.swaGray3 + " !important",
  },
  disableRowActions: {
    pointerEvents: "None !important",
    opacity: "0.5",
  },
  buttonStyle: {
    background: theme.palette.secondary.swaWhite,
  },
  exportButton: {
    marginRight: "10px",
    height: "40px",
  },
  rankStyle: {
    fontSize: "14px",
  },
  textInput: {
    maxWidth: "17vw",
    margin: "auto",
    textAlign: "left",
  },
  searchTextInput: {
    width: "200px",
  },
  searchedEmployee: {
    backgroundColor: theme.palette.primary.swaYellow,
  },
}));

const CreateIAMFTOBidGroup = (props) => {
  const classes = useStyles();

  const [isCreatingTheBidGroupDialogOpen, setIsCreatingTheBidGroupDialogOpen] = useState(false);
  const [isReloadingTheBidGroupDialogOpen, setIsReloadingTheBidGroupDialogOpen] = useState(false);
  const [isMissingPublishedSeniorityListDialogOpen, setIsMissingPublishedSeniorityListDialogOpen] = useState(false);
  const [isBidGroupClearAllDialogOpen, setIsBidGroupClearAllDialogOpen] = useState(false);

  const [isBidGroupLoading, setIsBidGroupLoading] = useState(false);
  const [bidGroupErrors, setBidGroupErrors] = useState("");

  //add or remove employee dialogs
  const [showAddEmployeeDialog, setShowAddEmployeeDialog] = useState(false);
  const [showRemoveEmployeeDialog, setShowRemoveEmployeeDialog] = useState(false);
  const [removeEmployeeData, setRemoveEmployeeData] = useState(null);
  const [showReAddEmployeeDialog, setShowReAddEmployeeDialog] = useState(false);
  const [reAddEmployeeData, setReAddEmployeeData] = useState(null);

  // Ineligible dialog
  const [isEligiblityDialogOpen, setIsEligiblityDialogOpen] = useState(false);
  const [eligiblityEmployeeIdx, setEligiblityEmployeeIdx] = useState(null);

  // cell edit warning dialogs
  const [invalidBidGroupRankDialog, setInvalidBidGroupRankDialog] = useState(false);
  const [invalidBalanceDialog, setInvalidBalanceDialog] = useState(false);
  let apiRef = useGridApiRef();

  //comments dialog
  const [selectedEmployeeForAudit, setSelectedEmployeeForAudit] = useState(null);

  // Search
  const [searchString, setSearchString] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [searchedEmployees, setSearchedEmployees] = useState([]);
  const [currSearchedEmployeesIdx, setCurrSearchedEmployeesIdx] = useState(-1);
  const [selectedBidGroupFilterRadioButton, setSelectedBidGroupFilterRadioButton] = useState(
    BID_GROUP_FILTER_OPTIONS.ALL
  );
  const [filteredBidGroup, setFilteredBidGroup] = useState([]);

  useEffect(() => {
    initializeSearchIndex(filteredBidGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBidGroup]);

  useEffect(() => {
    jumpToSearchedEmployee(apiRef, currSearchedEmployeesIdx, searchedEmployees, props.bidGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedEmployees, currSearchedEmployeesIdx]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const filteredRows =
        selectedBidGroupFilterRadioButton === BID_GROUP_FILTER_OPTIONS.MISSING_BALANCES
          ? props.bidGroup.filter((employee) => employee.balance === "-")
          : props.bidGroup;
      setFilteredBidGroup(filteredRows);
    }, 0); // Prevent MUI edit mode crash

    return () => clearTimeout(timeout);
  }, [props.bidGroup, selectedBidGroupFilterRadioButton]);

  const handleIsMissingPublishedSeniorityOkClick = () => {
    setIsMissingPublishedSeniorityListDialogOpen(false);
  };

  const handleInvalidBidGroupRankDialogOkClick = () => {
    //If we don't change bidgroup data, it's not rendering again and keeping the invalid rank values. So changing reference
    props.setBidGroup(copyJsonObject(props.bidGroup));
    setInvalidBidGroupRankDialog(false);
  };

  const handleInvalidBalanceDialogOkClick = () => {
    props.setBidGroup(copyJsonObject(props.bidGroup));
    setInvalidBalanceDialog(false);
  };

  const handleLoadBidGroupClicked = () => {
    if (props.bidGroup.length > 0) {
      setIsReloadingTheBidGroupDialogOpen(true);
    } else {
      setIsCreatingTheBidGroupDialogOpen(true);
    }
  };

  const handleBidGroupClearAll = (event) => {
    props.setBidGroup([]);
    props.setCalcDailyAllotments(copyJsonObject(calcDailyAllotmentInitialState));
    props.setAllotmentDayState([]);
    props.setIsSchedMonthOrYearChanged(false);
    setIsBidGroupClearAllDialogOpen(false);
  };

  const handleLoadBidGroup = () => {
    setIsBidGroupLoading(true);
    setBidGroupErrors("");
    props.setBidGroup([]);
    setIsSearch(false);
    setSearchString("");

    getLatestPubSLEvent(
      props.bid.station,
      props.bid.workgroup,
      GET_BID_SENIORITY_LIST_TYPE(
        getUnionContractGivenWorkgroup(props.bid.workgroup),
        props.bid.station,
        props.bid.bidType
      )
    )
      .then((res) => res.json())
      .then((response) => {
        if (response.seniorityList) {
          setIsBidGroupLoading(false);
          setIsCreatingTheBidGroupDialogOpen(false);
          setIsReloadingTheBidGroupDialogOpen(false);
          props.setIsBidGroupManuallyLoaded(true);
          if (response.seniorityList.length > 0) {
            const scrubbedSeniorityList = getScrubbedSeniorityListForFTO(response.seniorityList);
            props.setBidGroup(scrubbedSeniorityList);
          } else {
            setIsMissingPublishedSeniorityListDialogOpen(true);
          }
        } else {
          console.error("ERROR!");
          setBidGroupErrors("Unexpected Error!");
        }
      });
  };

  const handleAddEmployeeToBidGroup = () => {
    setShowAddEmployeeDialog(true);
  };

  const handleAddEmployeeDialogClose = () => {
    setShowAddEmployeeDialog(false);
  };

  const handleIneligibleClick = (eid) => {
    const clickedEmployeeIdx = props.bidGroup.findIndex((employee) => employee.eid === eid);
    setEligiblityEmployeeIdx(clickedEmployeeIdx);
    setIsEligiblityDialogOpen(true);
  };

  const handleEligibilityEmployeeDialogClose = () => {
    setIsEligiblityDialogOpen(false);
    setEligiblityEmployeeIdx(null);
  };

  const handleRemoveEmployee = (empData) => {
    setRemoveEmployeeData(empData);
    setShowRemoveEmployeeDialog(true);
  };

  const handleRemoveEmployeeDialogClose = () => {
    setShowRemoveEmployeeDialog(false);
  };

  const handleAddRemovedEmployee = (employeeData) => {
    setReAddEmployeeData(employeeData);
    setShowReAddEmployeeDialog(true);
  };

  const handleReAddEmployeeDialogClose = () => {
    setShowReAddEmployeeDialog(false);
  };

  const handleBidGroupFilterRadioButtonClicked = (event) => {
    setSearchString("");
    setSearchedEmployees([]);
    setCurrSearchedEmployeesIdx(-1);
    setSelectedBidGroupFilterRadioButton(event.target.value);
  };

  const renderCreatingTheBidGroupDialog = () => {
    return (
      <Dialog
        sx={{ width: "500px", margin: "auto" }}
        open={isCreatingTheBidGroupDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        <DialogTitle>Creating the Bid Group</DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"}>
            <Grid container>
              <Grid item xs={12} sx={{ paddingBottom: "20px" }}>
                The Bid Group will be created from the Last Published Classification Seniority List.
              </Grid>
              <Grid item xs={12} sx={{ paddingBottom: "20px" }}>
                Any changes made to the Bid Group will not affect the Seniority List.
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={() => setIsCreatingTheBidGroupDialogOpen(false)}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleLoadBidGroup} isLoading={isBidGroupLoading}>
            Load
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
        <Grid container sx={{ display: "flex", padding: "10px", alignItems: "center" }}>
          <Grid item xs={8}>
            <Typography
              variant="h6"
              sx={{ textAlign: "left", marginTop: "10px", marginLeft: "10px", fontWeight: "bold" }}
            >
              {props.bid.station} | {props.bid.workgroup}{" "}
              {props.schedMonth !== "" &&
                props.schedYear !== "" &&
                `| ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
              {props.bidName ? ` | ${props.bidName}` : ""}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: "flex", justifyContent: "right" }}>
            <BalancesImport bid={props.bid} bidGroup={props.bidGroup} setBidGroup={props.setBidGroup} />
            <BidGroupExportCsv bidGroup={props.bidGroup} bid={props.bid} />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <Alert severity="info">
              The Bid Group is{" "}
              <u>
                <b>not</b>
              </u>{" "}
              the Seniority List. Any changes made to the Bid Group will{" "}
              <u>
                <b>not</b>
              </u>{" "}
              affect the Seniority List.
            </Alert>
          </Grid>
        </Grid>

        <Grid container sx={{ bgcolor: "secondary.swaLightestBlue", padding: "10px" }} alignItems={"center"}>
          <Grid item xs={2.5} sx={{ textAlign: "left" }}>
            <Box sx={{ paddingLeft: "20px", paddingRight: "10px", paddingTop: "10px", paddingBottom: "10px" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleLoadBidGroupClicked}
                size="small"
                startIcon={<AddIcon />}
                disabled={
                  isDeletedBid(props.bid.status) ||
                  isOpenBid(props.bid.status) ||
                  isClosedBid(props.bid.status) ||
                  isAwardedBid(props.bid.status)
                }
                sx={{ minWidth: "162px" }}
              >
                Load Bid Group
              </Button>
            </Box>
          </Grid>
          <Grid item xs={5.5} sx={{ display: "flex", justifyContent: "left" }}>
            <Grid container direction="column">
              <Grid container direction="row" sx={{ textAlign: "left" }}>
                <Grid item xs={1.5}>
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    Eligible
                  </Typography>
                </Grid>
                <Grid item xs={1.5}>
                  Total:{" "}
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {getEligibleEmployees(props.bidGroup).length}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  Unranked:{" "}
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {getEmployeeUnrankedCount(getEligibleEmployees(props.bidGroup))}
                  </Typography>
                </Grid>
                <Grid item xs={3} sx={{ textAlign: "left" }}>
                  Missing Balances:{" "}
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {getEligibleEmployeesWithInvalidBalanceCount(getEligibleEmployees(props.bidGroup))}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container direction="row" sx={{ textAlign: "left" }}>
                <Grid item xs={1.5}>
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    Ineligible
                  </Typography>
                </Grid>
                <Grid item>
                  Total:{" "}
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {getIneligibleEmployees(props.bidGroup).length}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <Grid container>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "right", paddingBottom: "10px" }}>
                <BidGroupSearchBox
                  bid={props.bid}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  isSearch={isSearch}
                  setIsSearch={setIsSearch}
                  searchedEmployees={searchedEmployees}
                  setSearchedEmployees={setSearchedEmployees}
                  currSearchedEmployeesIdx={currSearchedEmployeesIdx}
                  setCurrSearchedEmployeesIdx={setCurrSearchedEmployeesIdx}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "right" }}>
                <Box sx={{ paddingLeft: "10px", paddingRight: "10px" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setIsBidGroupClearAllDialogOpen(true);
                    }}
                    size="small"
                    className={classes.buttonStyle}
                    disabled={
                      isDeletedBid(props.bid.status) ||
                      isOpenBid(props.bid.status) ||
                      isClosedBid(props.bid.status) ||
                      isAwardedBid(props.bid.status)
                    }
                  >
                    Clear All
                  </Button>
                </Box>
                <Box sx={{ paddingLeft: "10px", paddingRight: "10px" }}>
                  <Tooltip
                    title={
                      getEmployeeUnrankedCount(getEligibleEmployees(props.bidGroup)) > 0 &&
                      BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE
                    }
                    placement="bottom"
                    arrow
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleAddEmployeeToBidGroup}
                        size="small"
                        className={classes.buttonStyle}
                        startIcon={<AddIcon />}
                        disabled={
                          isDeletedBid(props.bid.status) ||
                          !props.bidGroup.length > 0 ||
                          getEmployeeUnrankedCount(getEligibleEmployees(props.bidGroup)) > 0 ||
                          isAwardedBid(props.bid.status)
                        }
                      >
                        Add Employee
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="row" sx={{ textAlign: "left", marginLeft: "20px" }}>
            <FormControl>
              <RadioGroup
                value={selectedBidGroupFilterRadioButton}
                onChange={handleBidGroupFilterRadioButtonClicked}
                row
                name="row-radio-buttons-group"
                sx={{ alignItems: "center" }}
              >
                <Typography
                  sx={{ marginRight: "10px", fontWeight: "bold", color: "secondary.swaBlack", fontSize: "14px" }}
                >
                  Bid Group Filter:{" "}
                </Typography>
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      marginRight: "5px",
                    },
                  }}
                  value={BID_GROUP_FILTER_OPTIONS.ALL}
                  control={<Radio />}
                  label={`All (${props.bidGroup.length})`}
                />
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      marginRight: "5px",
                    },
                  }}
                  value={BID_GROUP_FILTER_OPTIONS.MISSING_BALANCES}
                  control={<Radio />}
                  label={`Missing Balances (${
                    getEligibleEmployeesWithInvalidBalances(getEligibleEmployees(props.bidGroup)).length
                  })`}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container>
          <Box sx={{ height: `calc(85vh - 360px)`, width: "inherit" }}>
            <DataGridPro
              className={classes.employeeGroupList}
              sx={{ border: 0 }}
              apiRef={apiRef}
              columns={getBidGroupColumns(
                {
                  bidReferenceNumber: props.bid.referenceNumber,
                  bidGroup: props.bidGroup,
                  contract: getUnionContractGivenWorkgroup(props.bid.workgroup),
                  seniorityListType: GET_BID_SENIORITY_LIST_TYPE(
                    getUnionContractGivenWorkgroup(props.bid.workgroup),
                    props.bid.station,
                    props.bid.bidType
                  ),
                  workgroup: props.bid.workgroup,
                  bidStatus: props.bid.status,
                  rankStyle: classes.rankStyle,
                  initialBidGroup: getNonRemovedEmployees(props.initialBidGroup),
                  userSessionEid: props.userSession.eid,
                  bidStation: props.bid.station,
                },
                handleIneligibleClick,
                handleRemoveEmployee,
                handleAddRemovedEmployee,
                props.handleEmployeeChoicesAsAdmin,
                setSelectedEmployeeForAudit
              )}
              rows={filteredBidGroup}
              rowHeight={50}
              components={{
                LoadingOverlay: customLoadingTableOverlay,
                NoRowsOverlay: CustomNoRowsTableOverlay,
                ErrorOverlay: CustomErrorTableOverlay,
              }}
              componentsProps={{
                noRowsOverlay: { pageType: BID_GROUP_PAGE_TYPE },
                errorOverlay: {
                  errorMessage: bidGroupErrors,
                  errorCode: "TBD",
                },
              }}
              loading={props.isBidGroupLoading}
              rowReordering={!(isDeletedBid(props.bid.status) || isAwardedBid(props.bid.status))}
              onRowOrderChange={(params) => handleRowOrderChange(params, props.bidGroup, props.setBidGroup)}
              onCellFocusOut={(params, e) => {
                if (params.field === "rank") {
                  handleRankCellEditCommit(
                    params,
                    e,
                    props.bidGroup,
                    props.setBidGroup,
                    setInvalidBidGroupRankDialog,
                    apiRef
                  );
                }

                if (params.field === "balance") {
                  handleBalanceCellEditCommit(params, e, props.bidGroup, props.setBidGroup, setInvalidBalanceDialog);
                }
              }}
              onCellEditCommit={(params, e) => {
                if (params.field === "rank") {
                  handleRankCellEditCommit(
                    params,
                    e,
                    props.bidGroup,
                    props.setBidGroup,
                    setInvalidBidGroupRankDialog,
                    apiRef
                  );
                }

                if (params.field === "balance") {
                  handleBalanceCellEditCommit(params, e, props.bidGroup, props.setBidGroup, setInvalidBalanceDialog);
                }
              }}
              disableColumnMenu={true}
              showColumnVerticalBorder={true}
              showCellVerticalBorder={true}
              initialState={{
                pinnedColumns: {
                  left: ["__reorder__", "ineligible", "rank", "eid", "name", "balance"],
                  right: ["comments", "removeOrAddEmployee"],
                },
              }}
              getRowClassName={(params) =>
                searchedEmployees.some((row) => row.eid === params.row.eid)
                  ? classes.searchedEmployee
                  : params.row.isRemoved
                  ? classes.removedEmployeeRow
                  : params.row.isManuallyAddedToBidGroup
                  ? classes.newEmployee
                  : ""
              }
              getCellClassName={(params) => {
                // removed rows cannot be reordered
                if (
                  (params.field === "__reorder__" ||
                    params.field === "rank" ||
                    params.field === "name" ||
                    params.field === "balance") &&
                  params.row.isRemoved
                ) {
                  return classes.disableRowActions;
                }
                return "";
              }}
              getRowId={(row) => row.eid}
            />
          </Box>
        </Grid>
      </Grid>
      {renderCreatingTheBidGroupDialog()}
      <WarningDialog
        isDialogOpen={isMissingPublishedSeniorityListDialogOpen}
        handleClickDialogClose={handleIsMissingPublishedSeniorityOkClick}
        header={"Missing Published Seniority List"}
        message={`Please publish a Seniority List to load the Bid Group.`}
        severity={"error"}
      />
      <ConfirmationDialog
        isDialogOpen={isBidGroupClearAllDialogOpen}
        title="Clear the Bid Group"
        message={`Are you sure you want to remove all the Employees in this Bid Group?\n\nAny changes made to the Bid Group will not affect the Seniority List.`}
        handleNoButtonClicked={() => setIsBidGroupClearAllDialogOpen(false)}
        handleYesButtonClicked={handleBidGroupClearAll}
      />
      <ConfirmationDialog
        isDialogOpen={isReloadingTheBidGroupDialogOpen}
        title="Reloading the Bid Group"
        message="The current Bid Group will be overwritten."
        hasWarning={true}
        warningMessage={"This will overwrite and recalculate the allotments."}
        handleNoButtonClicked={() => setIsReloadingTheBidGroupDialogOpen(false)}
        handleYesButtonClicked={handleLoadBidGroup}
        isYesLoading={isBidGroupLoading}
        noTitle={"Cancel"}
        yesTitle={"Reload"}
      />
      <EmployeeEligibilityDialog
        isDialogOpen={isEligiblityDialogOpen}
        onClickDialogClose={handleEligibilityEmployeeDialogClose}
        bidGroup={props.bidGroup}
        setBidGroup={props.setBidGroup}
        employeeIdx={eligiblityEmployeeIdx}
        userSession={props.userSession}
        bidStatus={props.bid.status}
        bidType={props.bid.bidType}
      />
      <AddEmployeeDialog
        isDialogOpen={showAddEmployeeDialog}
        onClickDialogClose={handleAddEmployeeDialogClose}
        bidGroup={props.bidGroup}
        setBidGroup={props.setBidGroup}
        setShowAddEmployeeDialog={setShowAddEmployeeDialog}
        apiRef={apiRef}
        bidStatus={props.bid.status}
        userSession={props.userSession}
        bidType={props.bid.bidType}
      />
      <RemoveEmployeeDialog
        isDialogOpen={showRemoveEmployeeDialog}
        onClickDialogClose={handleRemoveEmployeeDialogClose}
        bidGroup={props.bidGroup}
        setBidGroup={props.setBidGroup}
        removeEmployeeData={removeEmployeeData}
        setRemoveEmployeeData={setRemoveEmployeeData}
        apiRef={apiRef}
        bidStatus={props.bid.status}
        userSession={props.userSession}
        bidType={props.bid.bidType}
      />
      <ReAddEmployeeDialog
        isDialogOpen={showReAddEmployeeDialog}
        onClickDialogClose={handleReAddEmployeeDialogClose}
        bidGroup={props.bidGroup}
        setBidGroup={props.setBidGroup}
        reAddEmployeeData={reAddEmployeeData}
        setReAddEmployeeData={setReAddEmployeeData}
        apiRef={apiRef}
        bidStatus={props.bid.status}
        userSession={props.userSession}
        bidType={props.bid.bidType}
      />
      <WarningDialog
        isDialogOpen={invalidBidGroupRankDialog}
        handleClickDialogClose={handleInvalidBidGroupRankDialogOkClick}
        header={ERROR_MESSAGE}
        message={INVALID_BID_GROUP_RANK}
        severity={"error"}
      />
      <WarningDialog
        isDialogOpen={invalidBalanceDialog}
        handleClickDialogClose={handleInvalidBalanceDialogOkClick}
        header={ERROR_MESSAGE}
        message={INVALID_FTO_BALANCE}
        severity={"error"}
      />
      <BidGroupEmpAuditHistoryDialog
        employee={selectedEmployeeForAudit}
        setEmployee={setSelectedEmployeeForAudit}
        bid={props.bid}
      />
    </>
  );
};

CreateIAMFTOBidGroup.propTypes = {
  bid: PropTypes.object.isRequired,
  bidGroup: PropTypes.array.isRequired,
  isBidGroupLoading: PropTypes.bool.isRequired,
  setBidGroup: PropTypes.func.isRequired,
  schedMonth: PropTypes.string.isRequired,
  schedYear: PropTypes.string.isRequired,
  setIsBidGroupManuallyLoaded: PropTypes.func.isRequired,
  setIsSchedMonthOrYearChanged: PropTypes.func.isRequired,
  bidName: PropTypes.string,
  setAllotmentDayState: PropTypes.func.isRequired,
  setCalcDailyAllotments: PropTypes.func.isRequired,
  initialBidGroup: PropTypes.array.isRequired,
  userSession: PropTypes.object.isRequired,
  handleEmployeeChoicesAsAdmin: PropTypes.func.isRequired,
};

export default CreateIAMFTOBidGroup;
